import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import downloadAsCsv from 'utils/downloadAsCsv'

import { getHealthCheckBillingData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider, useNoteBatchLoader } from '../Common/NoteBatchLoaderContext'
import styles from './Billing.module.scss'
import { ReactComponent as ExcelIcon } from './excel.svg'
import BillingRow from './Row'
import BillingTitleRow from './TitleRow'
import TotalRow from './TotalRow'
import GroupedRow from './GroupedRow'

function formatPrice(number) {
  return parseFloat(number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

const emailTypes = {
  SubsidiaryInvoiceForPayment: 'Payment',
  SubsidiaryInvoiceForReconciliation: 'Reconciliation',
  SubsidiaryInvoiceForPaymentLateMuch: 'Payment late'
}

const HealthCheckBilling = () => {
  const dispatch = useDispatch()

  const [results, setResults] = useState([])
  const [totalBarData, setTotalBarData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('billing')

  useEffect(() => {
    dispatch(getHealthCheckBillingData())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = useSelector(s => s.healthCheck.billings.isLoading)
  const hotelData = useSelector(s => s.healthCheck.billings.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.billings.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.billings.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(true)
  const [notesCacheRef, setNotesCacheRef] = useState(null)
  const [groupByHotelGroup, setGroupByHotelGroup] = useState(() => {
    const saved = localStorage.getItem('healthCheck-billing-groupByHotelGroup')
    return saved === 'true'
  })
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('healthCheck-billing-expandedGroups')
    return saved ? JSON.parse(saved) : {}
  })

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Calculate row status for Billing tab
  const calculateRowStatus = row => {
    const { balanceCurrent, balanceLate, paymentLastReceivedOn, isAutoPayEnabled, serviceBlockOn } = row

    const thirtyDaysAgo = moment().subtract(30, 'days')
    const hasRecentPayment =
      paymentLastReceivedOn && moment(paymentLastReceivedOn).isAfter(thirtyDaysAgo)

    // ALERT conditions:
    // 1. Service block date is set (hotel will be turned off)
    if (serviceBlockOn) {
      return 'alert'
    }

    // 2. High late balance (> $1000)
    if (balanceLate > 1000) {
      return 'alert'
    }

    // 3. High current balance (> $5000) without recent payment
    if (balanceCurrent > 5000 && !hasRecentPayment) {
      return 'alert'
    }

    // 4. Late balance > 0 and no recent payment
    if (balanceLate > 0 && !hasRecentPayment) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Some late balance but not critical (< $1000)
    if (balanceLate > 0 && balanceLate <= 1000) {
      return 'warning'
    }

    // 2. High current balance but has recent payment
    if (balanceCurrent > 5000 && hasRecentPayment) {
      return 'warning'
    }

    // 3. Moderate balance ($1000-$5000) without recent payment
    if (balanceCurrent >= 1000 && balanceCurrent <= 5000 && !hasRecentPayment) {
      return 'warning'
    }

    // GREEN conditions:
    // Low balance and up to date payments
    if (balanceCurrent < 1000 && balanceLate === 0 && (hasRecentPayment || isAutoPayEnabled)) {
      return 'green'
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const { balanceCurrent, balanceLate, paymentLastReceivedOn, isAutoPayEnabled, serviceBlockOn } = row

    const thirtyDaysAgo = moment().subtract(30, 'days')
    const hasRecentPayment =
      paymentLastReceivedOn && moment(paymentLastReceivedOn).isAfter(thirtyDaysAgo)

    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (serviceBlockOn) {
        const daysUntilBlock = moment(serviceBlockOn).diff(moment(), 'days')
        reasons.push(`Service will be blocked on ${moment(serviceBlockOn).format('MMM DD, YYYY')} (${daysUntilBlock} days)`)
      }
      if (balanceLate > 1000) {
        reasons.push(`High late balance: $${balanceLate.toLocaleString()}`)
      }
      if (balanceCurrent > 5000 && !hasRecentPayment) {
        reasons.push(`High current balance: $${balanceCurrent.toLocaleString()} (no recent payment)`)
      }
      if (balanceLate > 0 && !hasRecentPayment) {
        reasons.push(`Late balance: $${balanceLate.toLocaleString()} (no payment in 30+ days)`)
      }
    } else if (status === 'warning') {
      if (balanceLate > 0 && balanceLate <= 1000) {
        reasons.push(`Late balance: $${balanceLate.toLocaleString()}`)
      }
      if (balanceCurrent > 5000 && hasRecentPayment) {
        reasons.push(`High current balance: $${balanceCurrent.toLocaleString()} (has recent payment)`)
      }
      if (balanceCurrent >= 1000 && balanceCurrent <= 5000 && !hasRecentPayment) {
        reasons.push(`Moderate balance: $${balanceCurrent.toLocaleString()} (no recent payment)`)
      }
    } else if (status === 'green') {
      reasons.push(`Balance: $${balanceCurrent.toLocaleString()}`)
      if (balanceLate === 0) {
        reasons.push('No late balance')
      }
      if (hasRecentPayment || isAutoPayEnabled) {
        reasons.push(isAutoPayEnabled ? 'AutoPay enabled' : 'Recent payment received')
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        hideZeroBalance,
        hideInvalidPaymentDate,
        paymentDateFrom,
        paymentDateTo
      } = filterState

      const filtered = hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })
        .filter(h => {
          if (hideZeroBalance) {
            return h.balanceCurrent > 0
          }
          return true
        })
        .filter(h => {
          if (hideInvalidPaymentDate) {
            // Check if paymentLastReceivedOn is valid
            return h.paymentLastReceivedOn && moment(h.paymentLastReceivedOn).isValid()
          }
          return true
        })
        .filter(h => {
          // Filter by payment date range
          if (paymentDateFrom || paymentDateTo) {
            if (!h.paymentLastReceivedOn || !moment(h.paymentLastReceivedOn).isValid()) {
              return false // Exclude hotels without valid payment dates when filter is active
            }
            const paymentDate = moment(h.paymentLastReceivedOn)
            const fromDate = paymentDateFrom ? moment(paymentDateFrom).startOf('day') : null
            const toDate = paymentDateTo ? moment(paymentDateTo).endOf('day') : null
            
            if (fromDate && toDate) {
              return paymentDate.isBetween(fromDate, toDate, null, '[]') // inclusive
            } else if (fromDate) {
              return paymentDate.isSameOrAfter(fromDate)
            } else if (toDate) {
              return paymentDate.isSameOrBefore(toDate)
            }
          }
          return true
        })

      // Apply status filter
      return filtered.filter(h => {
        const status = calculateRowStatus(h)
        if (status === 'alert') return showAlert
        if (status === 'warning') return showWarning
        if (status === 'green') return showGreen
        return true
      })
    }

    function getSortedResults(hotelData, sortBy, sortDecreasing, notesCache = null) {
      // Map column indices to property names (matching TitleRow order)
      const titleRowNames = [
        'displayId',              // 0: Id
        'shortName',              // 1: Hotel name
        'csmUserFullName',        // 2: CSM
        'isAutoPayEnabled',       // 3: Autopay
        'balanceCurrent',         // 4: Balance
        'balanceLate',            // 5: Late balance
        'serviceBlockOn',         // 6: Service Block
        'paymentLastAmount',      // 7: Last payment amount
        'paymentLastReceivedOn',  // 8: Last payment date
        'emailLastSentOn',        // 9: Last email date
        'note'                    // 10: Notes
      ]

      const sortByProp = titleRowNames[sortBy]
      if (!sortByProp) {
        return hotelData // Return unsorted if invalid index
      }

      const sortedData = hotelData.sort((a, b) => {
        // Handle notes (stored in cache)
        if (sortByProp === 'note') {
          const aNoteKey = `${a.subsidiaryId}-healthCheck-billing`
          const bNoteKey = `${b.subsidiaryId}-healthCheck-billing`
          const aNote = notesCache?.get(aNoteKey) || ''
          const bNote = notesCache?.get(bNoteKey) || ''
          if (aNote === bNote) return 0
          const aUpper = aNote.toUpperCase()
          const bUpper = bNote.toUpperCase()
          return aUpper > bUpper ? 1 : -1
        }

        // Handle date fields
        const dateFields = ['paymentLastReceivedOn', 'emailLastSentOn', 'serviceBlockOn']
        if (dateFields.includes(sortByProp)) {
          const aDate = a[sortByProp] 
            ? (typeof a[sortByProp] === 'string' 
                ? Date.parse(a[sortByProp]) 
                : a[sortByProp].getTime 
                  ? a[sortByProp].getTime() 
                  : new Date(a[sortByProp]).getTime())
            : 0
          const bDate = b[sortByProp] 
            ? (typeof b[sortByProp] === 'string' 
                ? Date.parse(b[sortByProp]) 
                : b[sortByProp].getTime 
                  ? b[sortByProp].getTime() 
                  : new Date(b[sortByProp]).getTime())
            : 0
          return bDate - aDate
        }

        // Handle numeric fields (currency values)
        const numericFields = ['balanceCurrent', 'balanceLate', 'paymentLastAmount']
        if (numericFields.includes(sortByProp)) {
          const aVal = a[sortByProp] != null ? Number(a[sortByProp]) : 0
          const bVal = b[sortByProp] != null ? Number(b[sortByProp]) : 0
          return bVal - aVal
        }

        // Handle boolean fields
        if (typeof a[sortByProp] === 'boolean') {
          return Number(b[sortByProp]) - Number(a[sortByProp])
        }

        // Handle string fields
        if (typeof a[sortByProp] === 'string') {
          const aVal = a[sortByProp] || ''
          const bVal = b[sortByProp] || ''
          if (aVal === bVal) return 0
          const aUpper = aVal.toUpperCase()
          const bUpper = bVal.toUpperCase()
          return aUpper > bUpper ? 1 : -1
        }

        // Default numeric comparison
        const aVal = a[sortByProp] != null ? Number(a[sortByProp]) : 0
        const bVal = b[sortByProp] != null ? Number(b[sortByProp]) : 0
        return bVal - aVal
      })

      return sortDecreasing ? sortedData : sortedData.reverse()
    }

    function getResults(notesCache) {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        sortByIndex,
        sortDecreasing,
        notesCache
      )

      setResults(results)

      const totalBarData = results.reduce(
        (prev, curr) => {
          return {
            balanceCurrent: prev.balanceCurrent + curr.balanceCurrent,
            balanceLate: prev.balanceLate + curr.balanceLate,
            paymentAmount: prev.paymentAmount + curr.paymentLastAmount,
            bookingsCharged:
              prev.totalBookingValueToCharge + curr.totalBookingValueToCharge,
            bookingsNotCharged:
              prev.totalBookingValueNotCharged + curr.totalBookingValueNotCharged
          }
        },
        {
          balanceLate: 0,
          balanceCurrent: 0,
          paymentAmount: 0
        }
      )

      setTotalBarData({ ...totalBarData, count: results.length })
    }

    getResults(notesCacheRef)
  }, [filterState, hotelData, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen, notesCacheRef])

  // Prepare subsidiary IDs for batch loading
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-billing'
    }))
  }, [results])

  // Get notes cache for CSV export
  const BillingContent = () => {
    const { notesCache } = useNoteBatchLoader()
    
    // Update parent's notesCacheRef when cache is available
    useEffect(() => {
      if (notesCache) {
        setNotesCacheRef(notesCache)
      }
    }, [notesCache])

    function handleClickDownloadAsCsv() {
      // Get filter values for metadata
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds,
        hideZeroBalance,
        hideInvalidPaymentDate
      } = filterState

      // Helper function to get filter label
      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      // Get CSM names if available
      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      // Get status labels
      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        ['Hide Zero Balance', hideZeroBalance ? 'Yes' : 'No'],
        ['Hide Invalid Payment Date', hideInvalidPaymentDate ? 'Yes' : 'No'],
        ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
        [] // Empty row separator
      ]

      const titleRow = [
        'Hotel ID',
        'Hotel Name',
        'Hotel Group',
        'Brand',
        'Chain',
        'CSM',
        'Status',
        'Is AutoPay enabled',
        'Balance',
        'Late balance',
        'Service Block Date',
        'Last payment amount',
        'Last payment date',
        'Last email type',
        'Last email date',
        'Payment Method',
        'Billing Frequency',
        'Package Plans',
        'Package Fee',
        'Discount',
        'Monthly Fee (After Discount)',
        'Service Start Date',
        'Date Added',
        'Notes'
      ]

      const resAsArrays = results.map(b => {
        // Get note from cache if available
        const noteKey = `${b.subsidiaryId}-healthCheck-billing`
        const note = notesCache?.get(noteKey) || '-'

        return [
          b.displayId,
          `${b.brand}-${b.shortName}`,
          b.groupName || '-',
          b.parentChainCode || '-',
          b.chainCode || '-',
          b.csmUserFullName || '-',
          b.subsidiaryStatus ? (hotelStatusNamesFilterable[b.subsidiaryStatus] || b.subsidiaryStatus) : '-',
          b.isAutoPayEnabled ? 'Yes' : 'No',
          formatPrice(b.balanceCurrent),
          formatPrice(b.balanceLate),
          b.serviceBlockOn
            ? moment(b.serviceBlockOn).format('MM/DD/YYYY')
            : '-',
          formatPrice(b.paymentLastAmount),
          b.paymentLastReceivedOn
            ? moment(b.paymentLastReceivedOn).format('MM/DD/YYYY')
            : '-',
          b.emailLastContent ? emailTypes[b.emailLastContent] : '-',
          b.emailLastSentOn ? moment(b.emailLastSentOn).format('MM/DD/YYYY') : '-',
          b.paymentMethod || '-',
          b.paymentPeriod || '-',
          b.packagePlans || '-',
          formatPrice(b.packageFee),
          formatPrice(b.discount),
          formatPrice(b.monthlyFeeAfterDiscount),
          b.serviceStartDate
            ? moment(b.serviceStartDate).format('MM/DD/YYYY')
            : '-',
          b.addedOn
            ? moment(b.addedOn).format('MM/DD/YYYY')
            : '-',
          note
        ]
      })

      // Combine metadata, title row, and data
      const dataArray = [
        ...filterMetadata,
        titleRow,
        ...resAsArrays
      ]
      
      const fileName = `health_check_billing_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    }

  return (
      <>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus
        showCsmFilter
        showHideZeroBalanceToggle
        showHideInvalidPaymentDateToggle
        showPaymentDateFilter
      >
        <div className={styles.alertStatusControls}>
          <Toggle
            checked={groupByHotelGroup}
            handleChange={e => {
              const newValue = e.target.checked
              setGroupByHotelGroup(newValue)
              localStorage.setItem('healthCheck-billing-groupByHotelGroup', String(newValue))
            }}
            label="Group by Hotel Group"
            className={styles.toggle}
          />
          <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
          <Toggle
            className={styles.toggle}
            checked={showAlert}
            handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
            label="Alert"
          />
          <Toggle
            className={styles.toggle}
            checked={showWarning}
            handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
            label="Warning"
          />
          <Toggle
            className={styles.toggle}
            checked={showGreen}
            handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
            label="Green"
          />
        </div>
        <SecondaryRegularButton
          className={styles.copyToExcelButton}
            onClick={handleClickDownloadAsCsv}
        >
          <ExcelIcon /> Download as CSV
        </SecondaryRegularButton>
      </HealthCheckFilters>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={150} />
        ) : (
          <Table
            widths={[40, 240, 100, 100, 100, 110, 115, 125]}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <BillingTitleRow />
            {groupByHotelGroup ? (() => {
              // Group results by hotel group
              const grouped = results.reduce((acc, hotel) => {
                const groupName = hotel.groupName || 'Ungrouped'
                if (!acc[groupName]) {
                  acc[groupName] = []
                }
                acc[groupName].push(hotel)
                return acc
              }, {})

              // Sort groups alphabetically
              const sortedGroups = Object.keys(grouped).sort()

              let globalIndex = 1
              return sortedGroups.map(groupName => {
                const groupHotels = grouped[groupName]
                const groupKey = groupName || 'ungrouped'
                const isExpanded = expandedGroups[groupKey] !== false // Default to expanded

                return (
                  <GroupedRow
                    key={groupKey}
                    groupName={groupName}
                    count={groupHotels.length}
                    isExpanded={isExpanded}
                    onToggle={() => {
                      setExpandedGroups(prev => {
                        const newState = {
                          ...prev,
                          [groupKey]: !isExpanded
                        }
                        localStorage.setItem('healthCheck-billing-expandedGroups', JSON.stringify(newState))
                        return newState
                      })
                    }}
                  >
                    {groupHotels.map(h => {
                      const rowStatus = calculateRowStatus(h)
                      const statusExplanation = getStatusExplanation(h)
                      const currentIndex = globalIndex++
                      return (
                        <BillingRow
                          key={h.subsidiaryId}
                          {...h}
                          index={currentIndex}
                          rowStatus={rowStatus}
                          statusExplanation={statusExplanation}
                        />
                      )
                    })}
                  </GroupedRow>
                )
              })
            })() : (
              results.map((h, i) => {
                const rowStatus = calculateRowStatus(h)
                const statusExplanation = getStatusExplanation(h)
                return (
                  <BillingRow
                    key={h.subsidiaryId}
                    {...h}
                    index={i + 1}
                    rowStatus={rowStatus}
                    statusExplanation={statusExplanation}
                  />
                )
              })
            )}
            <TotalRow {...totalBarData} />
          </Table>
        )}
      </div>
      </>
    )
  }

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-billing"
    >
      <div className={styles.wrapper}>
        <CustomHeader title="Hotel health check" width={1180} isFixed />
        <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
      </Tab.Parent>
      <BillingContent />
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckBilling
